import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { sampleLogo, schoolLogoNeedsPanel } from '../ic-footer-theme';

/** A school's logo URL, or '' when it has none. */
export function schoolLogo(group: any): string {
  return group?.group_logo || group?.group_base_logo || '';
}

/**
 * The network's schools/groups as a row of logos inside the new footer (shown only when the network has groups).
 * Like the network logo, a logo gets the light panel only when it would blend into the footer background
 * (see-through logos only: one on its own solid background always shows);
 * that check loads the logos again, so it waits until the footer is on screen (no cost to page load).
 */
@Component({
  selector: 'app-ic-footer-schools',
  templateUrl: './ic-footer-schools.component.html',
  styleUrls: ['./ic-footer-schools.component.scss'],
})
export class IcFooterSchoolsComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() groups: any[] = [];
  @Input() label: string;
  /** Footer background colour (--ftr-bg), to judge whether a logo needs the panel. */
  @Input() background = '';
  @Output() selectGroup = new EventEmitter<any>();

  readonly logoOf = schoolLogo;
  /** Logos that need the light panel, by URL. */
  panel: Record<string, boolean> = {};
  private visible = false;
  private sampled = new Set<string>();
  private observer?: IntersectionObserver;

  constructor(private host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) {}

  ngOnChanges(): void {
    if (this.visible) this.checkPanels();
  }

  ngAfterViewInit(): void {
    if (typeof IntersectionObserver === 'undefined') return;
    this.observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      this.observer?.disconnect();
      this.visible = true;
      this.checkPanels();
    }, { rootMargin: '200px' });
    this.observer.observe(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private checkPanels(): void {
    const background = this.background;
    (this.groups || []).map(schoolLogo).filter(url => url && !this.sampled.has(url + '|' + background)).forEach(url => {
      this.sampled.add(url + '|' + background);
      sampleLogo(url).then(sample => {
        const needs = schoolLogoNeedsPanel(sample, background);
        if (needs !== !!this.panel[url]) {
          this.panel = { ...this.panel, [url]: needs };
          this.cdr.markForCheck();
        }
      });
    });
  }

  /** A long row scrolls sideways with the normal mouse wheel too (its scrollbar is hidden); at either end the page scrolls. */
  onWheel(event: WheelEvent): void {
    const row = event.currentTarget as HTMLElement;
    if (!row || Math.abs(event.deltaX) > Math.abs(event.deltaY) || row.scrollWidth <= row.clientWidth) return;
    const atStart = row.scrollLeft <= 0 && event.deltaY < 0;
    const atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1 && event.deltaY > 0;
    if (atStart || atEnd) return;
    row.scrollLeft += event.deltaY;
    event.preventDefault();
  }
}
